import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaCoins } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const DividendYieldCalculator = () => {
  const [stockPrice, setStockPrice] = useState(500);
  const [annualDividend, setAnnualDividend] = useState(10);
  const [sharesOwned, setSharesOwned] = useState(100);
  const [dividendGrowthRate, setDividendGrowthRate] = useState(5);
  const [yearsToProject, setYearsToProject] = useState(10);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [stockPrice, annualDividend, sharesOwned, dividendGrowthRate, yearsToProject]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const price = Number(stockPrice) || 0;
    const dividend = Number(annualDividend) || 0;
    const shares = Number(sharesOwned) || 0;
    const growthRate = Number(dividendGrowthRate) || 0;
    const years = Number(yearsToProject) || 0;
    
    if (price <= 0) {
      setResult(null);
      return;
    }

    const dividendYield = (dividend / price) * 100;
    const totalInvestment = price * shares;
    const annualDividendIncome = dividend * shares;
    const monthlyDividendIncome = annualDividendIncome / 12;
    
    // Project future dividends
    const projectionData = [];
    let cumulativeDividend = 0;
    let currentDividend = dividend;
    
    for (let year = 1; year <= years; year++) {
      const yearDividendPerShare = currentDividend;
      const yearDividendTotal = yearDividendPerShare * shares;
      cumulativeDividend += yearDividendTotal;
      
      // Yield on cost (based on original purchase price)
      const yieldOnCost = (yearDividendPerShare / price) * 100;
      
      projectionData.push({
        year,
        dividendPerShare: yearDividendPerShare.toFixed(2),
        totalDividend: Math.round(yearDividendTotal),
        cumulativeDividend: Math.round(cumulativeDividend),
        yieldOnCost: yieldOnCost.toFixed(2),
      });
      
      // Apply dividend growth rate
      currentDividend = currentDividend * (1 + growthRate / 100);
    }
    
    // Payback period (years to recover investment through dividends)
    let paybackYears = 0;
    let cumulative = 0;
    currentDividend = dividend;
    while (cumulative < totalInvestment && paybackYears < 100) {
      cumulative += currentDividend * shares;
      currentDividend = currentDividend * (1 + growthRate / 100);
      paybackYears++;
    }

    setResult({
      dividendYield,
      totalInvestment,
      annualDividendIncome,
      monthlyDividendIncome,
      projectionData,
      paybackYears: paybackYears < 100 ? paybackYears : 'N/A',
      totalDividendIn10Years: cumulativeDividend,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Dividend Yield Calculator Report', {
      'Stock Price': formatCurrency(stockPrice),
      'Annual Dividend per Share': formatCurrency(annualDividend),
      'Shares Owned': sharesOwned,
      'Total Investment': formatCurrency(result.totalInvestment),
      'Dividend Yield': `${result.dividendYield.toFixed(2)}%`,
      'Annual Dividend Income': formatCurrency(result.annualDividendIncome),
      'Monthly Income': formatCurrency(result.monthlyDividendIncome),
      'Dividend Growth Rate': `${dividendGrowthRate}%`,
      [`Total Dividend (${yearsToProject} years)`]: formatCurrency(result.totalDividendIn10Years),
      'Payback Period': typeof result.paybackYears === 'number' ? `${result.paybackYears} years` : result.paybackYears,
    });
  };

  const handleReset = () => {
    setStockPrice(500);
    setAnnualDividend(10);
    setSharesOwned(100);
    setDividendGrowthRate(5);
    setYearsToProject(10);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Dividend Yield Calculator"
        description="Free Dividend Yield Calculator - Calculate annual dividend yield percentage on stocks."
        keywords="dividend yield calculator, dividend calculator, stock dividend calculator, dividend yield formula, dividend income calculator"
        canonicalPath="/dividend-yield"
      />
      <div className="calculator-header">
        <h1><FaCoins style={{ color: '#f59e0b' }} /> Dividend Yield Calculator</h1>
        <p>Calculate dividend yield and project future dividend income</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Current Stock Price (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={stockPrice === '' ? '' : stockPrice}
                onChange={(e) => setStockPrice(e.target.value === '' ? '' : Number(e.target.value))}
                min="0.01"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Annual Dividend per Share (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={annualDividend === '' ? '' : annualDividend}
                onChange={(e) => setAnnualDividend(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="0.1"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Number of Shares Owned</label>
            <input
              type="number"
              value={sharesOwned === '' ? '' : sharesOwned}
              onChange={(e) => setSharesOwned(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="10000"
                step="10"
                value={sharesOwned || 0}
                onChange={(e) => setSharesOwned(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Expected Dividend Growth Rate (%)</label>
            <input
              type="number"
              value={dividendGrowthRate === '' ? '' : dividendGrowthRate}
              onChange={(e) => setDividendGrowthRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              max="50"
              step="0.5"
            />
          </div>

          <div className="input-group">
            <label>Years to Project</label>
            <input
              type="number"
              value={yearsToProject === '' ? '' : yearsToProject}
              onChange={(e) => setYearsToProject(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="30"
            />
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                <h3>Dividend Yield</h3>
                <div className="amount">{result.dividendYield.toFixed(2)}%</div>
                <small>Annual return from dividends</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#ecfdf5', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Annual Income</span>
                  <span className="value" style={{ color: '#059669' }}>{formatCurrency(result.annualDividendIncome)}</span>
                </div>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Monthly Income</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.monthlyDividendIncome)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Payback Period</span>
                  <span className="value">{typeof result.paybackYears === 'number' ? `${result.paybackYears} years` : result.paybackYears}</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Total Dividend ({yearsToProject} Years)</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{formatCurrency(result.totalDividendIn10Years)}</span>
                </div>
              </div>

              {/* Dividend Projection */}
              <div className="breakup-section">
                <h4>Dividend Income Projection</h4>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>DPS (₹)</th>
                        <th>Income</th>
                        <th>Cumulative</th>
                        <th>YOC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.projectionData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.year}</td>
                          <td>₹{row.dividendPerShare}</td>
                          <td style={{ color: '#10b981' }}>{formatCurrency(row.totalDividend)}</td>
                          <td>{formatCurrency(row.cumulativeDividend)}</td>
                          <td style={{ color: '#8b5cf6' }}>{row.yieldOnCost}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <small style={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}>
                  DPS = Dividend Per Share, YOC = Yield on Cost
                </small>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Understanding Dividend Yield</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li><strong>Dividend Yield</strong> = (Annual Dividend / Stock Price) × 100</li>
              <li><strong>Yield on Cost</strong> = Dividend based on your purchase price</li>
              <li>Higher yield isn't always better - check company fundamentals</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Dividends in India are taxable as per your income slab</li>
              <li>TDS of 10% if dividend exceeds ₹5,000/year</li>
              <li>Look for companies with consistent dividend growth</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DividendYieldCalculator;
